// =============================================================================
// FILE: src/providers/QueryProvider.jsx
// MODULE: Core — TanStack Query Provider
// PROJECT: Arcus Sports Management
//
// DESCRIPTION:
//   Configures and provides the TanStack Query client to the entire app.
//   All useQuery / useMutation hooks in the app depend on this provider.
//
// CACHE STRATEGY:
//   staleTime   → how long data is considered fresh (no background refetch)
//   gcTime      → how long inactive query data stays in memory
//
//   Tournaments / standings: 60s stale — change infrequently
//   Matches in progress:     0s stale  — hooks override this for live data
//   User profile:           5min stale — rarely changes
//
// DEVTOOLS:
//   TanStack Query DevTools are shown only in development.
//   They appear as a floating panel in the bottom-right corner.
// =============================================================================

import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'

// ---------------------------------------------------------------------------
// Create the QueryClient with sensible defaults for a sports management app.
// These defaults can be overridden per-query in individual useQuery calls.
// ---------------------------------------------------------------------------
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // 60 seconds before data is considered stale — good for tournament data
      staleTime: 60 * 1000,

      // Keep unused data in cache for 5 minutes before garbage collection
      gcTime: 5 * 60 * 1000,

      // Retry failed queries up to 2 times before showing an error state
      retry: 2,

      // Exponential backoff: 1s, 2s max delays between retries
      retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 2000),

      // Refetch when the user returns to the browser tab
      refetchOnWindowFocus: true,

      // Do NOT refetch on component remount — prevents unnecessary API calls
      refetchOnMount: false,
    },
    mutations: {
      // Do not retry mutations — a failed write should fail visibly
      retry: 0,
    },
  },
})

// ---------------------------------------------------------------------------
// QueryProvider component
// Wrap this around the app in main.jsx.
// ---------------------------------------------------------------------------
export function QueryProvider({ children }) {
  return (
    <QueryClientProvider client={queryClient}>
      {children}

      {/* DevTools panel — only included in development builds via Vite tree-shaking */}
      {import.meta.env.VITE_ENV === 'development' && (
        <ReactQueryDevtools
          initialIsOpen={false}
          buttonPosition="bottom-right"
        />
      )}
    </QueryClientProvider>
  )
}

// Export the client for manual cache invalidation in mutation callbacks
export { queryClient }
